/**
 * 对局引擎 —— 状态初始化与推进
 *
 * 采用 reducer 风格：
 *   createGame(config) → GameState
 *   apply(state, action) → GameState（新状态，原状态不变）
 *
 * 随机性通过 GameConfig.random 注入，测试时可传固定序列复现。
 */

import {
  COPIES_PER_TILE,
  NUM_TILES,
  createWall,
  shuffle,
  tileIndex,
  type Tile,
} from '../tile.js';
import type { Mentsu } from '../agari.js';
import type {
  Action,
  GameState,
  PlayerState,
  RoundInfo,
  Seat,
  Wall,
} from './state.js';
import {
  DEAD_WALL_SIZE,
  INITIAL_HAND_SIZE,
  RIICHI_STICK_VALUE,
  STARTING_SCORE,
  dealerSeat,
  nextSeat,
} from './state.js';
import type { Wind } from '../yaku/types.js';
import { emptyTracker } from './furiten.js';
import { DEFAULT_RULE, type RuleConfig } from './rule-config.js';
import * as _flow from './flow.js';
const settleDrawModule = _flow;

// ════════════════════════════════════════════════════════════════
// 配置
// ════════════════════════════════════════════════════════════════

/** 结算模块（延迟加载，避免 engine ↔ detail 循环依赖）*/
import * as _detail from './detail.js';
const detailModule = _detail;

/** 对局配置 */
export interface GameConfig {
  /** 场风 */
  roundWind: Wind;
  /** 第几局 */
  roundNumber: 1 | 2 | 3 | 4;
  /** 本场数 */
  honba?: number;
  /** 供托数 */
  riichiSticks?: number;
  /** 各家起始点数（默认 25000） */
  scores?: readonly number[];
  /** 赤宝牌配置（默认每种花色 1 张） */
  redFives?: { m: number; p: number; s: number };
  /** 随机源（默认 Math.random；测试时注入固定序列） */
  random?: () => number;
  /**
   * 规则变体（天凤 / 雀魂 预设可切换）
   *
   * 缺省 = 天凤预设。规则函数一律通过 `rulesOf(state)` 读取。
   */
  rules?: RuleConfig;
}

// ════════════════════════════════════════════════════════════════
// 初始化
// ════════════════════════════════════════════════════════════════

/**
 * 创建新对局
 *
 * 流程：
 *   1. 建立 136 张牌山并洗牌
 *   2. 分配 4 家各 13 张手牌
 *   3. 剩余牌：前 4 张作岭上，最后 2 张作宝牌/里宝牌指示牌
 *   4. 庄家先摸牌（进入 discard 阶段）
 */
export function createGame(config: GameConfig): GameState {
  const random = config.random ?? Math.random;
  const round: RoundInfo = {
    roundWind: config.roundWind,
    roundNumber: config.roundNumber,
    honba: config.honba ?? 0,
    riichiSticks: config.riichiSticks ?? 0,
  };

  // ── 洗牌 ──
  const fullWall = shuffle(createWall(config.redFives ?? { m: 1, p: 1, s: 1 }), random);

  // ── 分配 ──
  // 按立直麻将标准：岭上 4 张（从牌山末尾扣），宝牌指示牌 1 张
  // 简化实现：末尾 6 张中，2 张作宝牌/里宝牌指示，4 张作岭上
  const wallTiles = fullWall.slice(0, NUM_TILES - 6);
  const doraArea = fullWall.slice(NUM_TILES - 6); // 6 张
  const doraIndicators = [doraArea[0]];
  const uraIndicators = [doraArea[1]];
  const dead = doraArea.slice(2); // 4 张岭上牌

  // ── 发牌（13 张 × 4 家，从前往后取）──
  const hands: Tile[][] = [[], [], [], []];
  let cursor = 0;
  for (let round13 = 0; round13 < INITIAL_HAND_SIZE; round13++) {
    for (let s = 0; s < 4; s++) {
      hands[s].push(wallTiles[cursor++]);
    }
  }

  const live = wallTiles.slice(cursor);

  const wall: Wall = {
    live,
    dead,
    doraIndicators,
    uraIndicators,
    doraRevealed: 1,
  };

  // ── 玩家状态 ──
  const scores = config.scores ?? [STARTING_SCORE, STARTING_SCORE, STARTING_SCORE, STARTING_SCORE];
  const players: PlayerState[] = [0, 1, 2, 3].map((s) => ({
    seat: s as Seat,
    hand: hands[s].sort(compareTile),
    melds: [],
    discards: [],
    riichi: false,
    riichiTurn: null,
    riichiDiscardIndex: null,
    furiten: false,
    score: scores[s],
    won: false,
    tenpaiAtDraw: false,
  }));

  // ── 庄家摸第一张 ──
  const dealer = dealerSeat(round);
  const baseState: GameState = {
    phase: 'draw',
    round,
    players,
    activeSeat: dealer,
    turn: 1,
    wall,
    lastDiscard: null,
    result: null,
    drawnTileId: null,
    // 规则变体落到状态上（后续局面 spread 时自动继承）
    rules: config.rules ?? DEFAULT_RULE,
    // ⚠️ 必须初始化振听追踪器（否则新局后 driver 访问 temporaryFuriten 报错）
    furitenTracker: emptyTracker(),
  };

  return apply(baseState, { type: 'draw' });
}

// ════════════════════════════════════════════════════════════════
// 动作应用（reducer）
// ════════════════════════════════════════════════════════════════

/**
 * 应用一个动作，返回新状态
 *
 * ⚠️ 不做合法性校验（由 canApply 负责），调用前应先校验。
 */
export function apply(state: GameState, action: Action): GameState {
  switch (action.type) {
    case 'draw':
      return doDraw(state);
    case 'discard':
      return doDiscard(state, action.tile, action.riichi ?? false);
    case 'tsumo':
      return doTsumo(state);
    case 'ron':
      return doRon(state);
    case 'chi':
      return doMeld(state, 'chi', action.tiles);
    case 'pon':
      return doMeld(state, 'pon', action.tiles);
    case 'kan':
      return doKan(state, action.kind, action.tile);
    case 'pass':
      return doPass(state);
    case 'riichi_declare':
      return state; // 立直在 discard 时一并处理
    case 'abortive_draw':
      return abortiveDraw(state, action.reason);
    default: {
      const _exhaustive: never = action;
      throw new Error(`未知动作: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

// ════════════════════════════════════════════════════════════════
// 各动作实现
// ════════════════════════════════════════════════════════════════

/** 摸牌 */
function doDraw(state: GameState): GameState {
  if (state.wall.live.length === 0) {
    // 牌山已空 → 荒牌流局
    // ⚠️ 必须算听牌罚符（踩过：写死 [0,0,0,0] 导致流局不结算）
    //    规则：听牌者向未听牌者收取，总罚符恒定 3000
    const tenpai = settleDrawModule.tenpaiSeats(state);
    const penalty = settleDrawModule.calculateDrawPenalty(tenpai);
    const players = state.players.map((p) => ({
      ...p,
      score: p.score + (penalty[p.seat] ?? 0),
    }));

    return {
      ...state,
      phase: 'over',
      players,
      result: {
        kind: 'draw',
        reason: 'exhaustive',
        tenpaiSeats: tenpai,
        scoreChanges: penalty,
      },
    };
  }

  const [drawn, ...rest] = state.wall.live;
  const seat = state.activeSeat;

  const players = state.players.map((p) =>
    p.seat === seat ? { ...p, hand: [...p.hand, drawn].sort(compareTile) } : p,
  );

  return {
    ...state,
    phase: 'discard',
    players,
    wall: { ...state.wall, live: rest },
    drawnTileId: drawn.id, // UI 用（排序后无法靠位置推断）
  };
}

/** 打牌 */
function doDiscard(state: GameState, tile: Tile, riichi: boolean): GameState {
  const seat = state.activeSeat;

  const players = state.players.map((p) => {
    if (p.seat !== seat) return p;
    // 移除该牌（按 id 匹配，因为可能有多张相同的牌）
    const idx = p.hand.findIndex((t) => t.id === tile.id);
    if (idx < 0) throw new Error(`手牌中没有这张牌: ${JSON.stringify(tile)}`);
    const hand = [...p.hand.slice(0, idx), ...p.hand.slice(idx + 1)];
    return {
      ...p,
      hand,
      discards: [...p.discards, tile],
      riichi: riichi || p.riichi,
      riichiTurn: riichi && p.riichiTurn === null ? state.turn : p.riichiTurn,
      // 立直宣言时记录这张牌在舍牌数组中的下标（此后不再变化）
      riichiDiscardIndex:
        riichi && p.riichiDiscardIndex === null ? p.discards.length : p.riichiDiscardIndex,
      score: riichi && !p.riichi ? p.score - RIICHI_STICK_VALUE : p.score,
    };
  });

  const newState: GameState = {
    ...state,
    phase: 'claim',
    players,
    lastDiscard: { tile, bySeat: seat },
    round: riichi
      ? { ...state.round, riichiSticks: state.round.riichiSticks + 1 }
      : state.round,
  };

  // ⚠️ 途中流局（自动判定，弃牌后立即结算并结束本局）
  //    · 四家立直：本次打牌同时宣言立直 → 四家全立直
  //    · 四风连打：四家第一巡各打同一风牌且无人鸣牌
  if (riichi && settleDrawModule.checkFourRiichi(newState)) {
    return abortiveDraw(newState, 'four_riichi');
  }
  if (settleDrawModule.checkFourWinds(newState)) {
    return abortiveDraw(newState, 'four_winds');
  }

  // 简化：无自动鸣牌 → 直接进入下一家摸牌
  // （完整的鸣牌判定在 flow.ts 中由 AI/玩家决策驱动）
  return newState;
}

/** 途中流局：构造结束态（无罚符、供托保留） */
function abortiveDraw(
  state: GameState,
  reason: 'nine_terminals' | 'four_winds' | 'four_riichi' | 'four_kans' | 'triple_ron',
): GameState {
  const result = settleDrawModule.settleDraw(state, reason);
  return { ...state, phase: 'over', result };
}

/**
 * 自摸和牌
 *
 * ⚠️ 结算必须在这里完成（早期版本只填 [0,0,0,0]，
 *    导致 Web UI 直接调 apply 时点数变化全是 0）。
 */
function doTsumo(state: GameState): GameState {
  const seat = state.activeSeat;
  const p = state.players[seat];
  // ⚠️ 和了牌必须用 drawnTileId 定位 —— doDraw 会对手牌排序，
  //    手牌末张【不一定是】刚摸的牌（旧代码用 hand[length-1]，
  //    会让符数 / 待ち型 / 平和判定取错牌）。
  const winTile =
    (state.drawnTileId != null ? p.hand.find((t) => t.id === state.drawnTileId) : undefined) ??
    p.hand[p.hand.length - 1];

  const settled = settleWinInState(state, seat, 'tsumo', null, winTile);

  return {
    ...settled.state,
    phase: 'over',
    players: settled.state.players.map((p) =>
      p.seat === seat ? { ...p, won: true } : p,
    ),
    result: {
      kind: 'tsumo',
      winner: seat,
      winTile,
      scoreChanges: settled.changes,
    },
  };
}

/** 荣和 */
function doRon(state: GameState): GameState {
  const seat = state.activeSeat;
  const discard = state.lastDiscard;
  if (!discard) throw new Error('荣和时没有可荣和的牌');

  const settled = settleWinInState(state, seat, 'ron', discard.bySeat, discard.tile);

  return {
    ...settled.state,
    phase: 'over',
    players: settled.state.players.map((p) =>
      p.seat === seat ? { ...p, won: true } : p,
    ),
    result: {
      kind: 'ron',
      winner: seat,
      loser: discard.bySeat,
      winTile: discard.tile,
      scoreChanges: settled.changes,
    },
  };
}

/** 鸣牌（吃/碰） */
function doMeld(
  state: GameState,
  kind: 'chi' | 'pon',
  tiles: readonly Tile[] = [],
): GameState {
  const seat = state.activeSeat;
  const discard = state.lastDiscard;
  if (!discard) throw new Error('鸣牌时没有可鸣的牌');

  const player = state.players[seat];
  const discardIdx = tileIndex(discard.tile);

  // 碰：从手牌取 2 张同种牌；吃：使用调用方指定的 2 张
  const chosen: Tile[] =
    kind === 'pon'
      ? player.hand.filter((t) => tileIndex(t) === discardIdx).slice(0, 2)
      : [...tiles];

  if (chosen.length !== 2) {
    throw new Error(
      kind === 'pon'
        ? `碰失败：手牌中 ${indexToName(discardIdx)} 不足 2 张`
        : '吃需要指定 2 张手牌',
    );
  }

  const usedIds = new Set(chosen.map((t) => t.id));
  const remaining = player.hand.filter((t) => !usedIds.has(t.id));

  // 顺子的 start 必须是**最小牌**的索引（tileIndex 返回该牌自身的索引，
  // 弃牌可能是顺子的中间或最大牌，所以要取所有参与牌的最小值）
  const allIdx = [...chosen.map(tileIndex), discardIdx];
  const start = kind === 'chi' ? Math.min(...allIdx) : tileIndex(discard.tile);

  const mentsu: Mentsu = {
    kind: kind === 'chi' ? 'shuntsu' : 'koutsu',
    start,
    open: true,
    names: [],
    // 被鸣那那张牌在面子中的位置（吃：最小/中间/最大牌皆可能）
    calledIndex: kind === 'chi' ? discardIdx - start : 0,
  };

  const players = state.players.map((p) => {
    if (p.seat === seat) {
      // 鸣牌者：手牌减少，副露增加。
      // ⚠️ 被鸣的弃牌**不记入任何人的河**（它已存在于副露中），
      // 否则牌数统计会重复计算（实测导致 137 ≠ 136）。
      return {
        ...p,
        hand: remaining,
        melds: [...p.melds, mentsu],
      };
    }
    // 被鸣牌者：河里移除最后一张（该牌已转为其副露的一部分）
    if (p.seat === discard.bySeat) {
      return { ...p, discards: p.discards.slice(0, -1) };
    }
    return p;
  });

  // 包牌（責任払い）：本次鸣牌若凑齐大三元/大四喜 → 记下责任者（被鸣牌者）
  //   暗杠不构成包牌，故仅碰（pon）触发；明杠在 doKan 处理。
  const paoClaim =
    kind === 'pon' ? detectPao(seat, discard.bySeat, players[seat].melds) : null;

  return {
    ...state,
    phase: 'discard', // 鸣牌后直接进入打牌
    activeSeat: seat,
    players,
    lastDiscard: null,
    pao: paoClaim ? [...(state.pao ?? []), paoClaim] : state.pao,
  };
}

/** 三元牌索引（白=31/發=32/中=33） */
const DRAGON_IDX = [31, 32, 33];
/** 风牌索引（東=27/南=28/西=29/北=30） */
const WIND_IDX = [27, 28, 29, 30];

/**
 * 包牌判定：鸣牌后若该家已凑齐 ≥3 个三元牌刻子 或 ≥4 个风牌刻子 → 成立包牌。
 * @returns 责任归属（无则 null）
 */
function detectPao(
  seat: Seat,
  liable: Seat,
  melds: readonly Mentsu[],
): import('./state.js').PaoClaim | null {
  const countOf = (idxs: readonly number[]) =>
    idxs.filter((i) =>
      melds.some((m) => (m.kind === 'koutsu' || m.kind === 'kantsu') && m.start === i),
    ).length;
  if (countOf(DRAGON_IDX) >= 3) return { owner: seat, liable, kind: 'daisangen' };
  if (countOf(WIND_IDX) >= 4) return { owner: seat, liable, kind: 'daisuushii' };
  return null;
}

/** 杠 */
function doKan(
  state: GameState,
  kind: 'ankan' | 'minkan' | 'kakan',
  tile: Tile,
): GameState {
  const seat = state.activeSeat;
  const player = state.players[seat];
  const idx = tileIndex(tile);

  let hand = [...player.hand];
  const melds = [...player.melds];

  if (kind === 'kakan') {
    // 加杠：手牌只取 1 张（第 4 张），把**已有的碰升级为杠**
    //   ⚠️ 不能沿用「从手牌取 3 张」——加杠时另 3 张在副露里，手里只有 1 张
    const meldIdx = melds.findIndex((m) => m.kind === 'koutsu' && m.start === idx);
    if (meldIdx < 0) throw new Error('加杠失败：没有对应的碰');
    const handIdx = hand.findIndex((t) => tileIndex(t) === idx);
    if (handIdx < 0) throw new Error('加杠失败：手中没有第 4 张');
    hand.splice(handIdx, 1);
    melds[meldIdx] = { ...melds[meldIdx], kind: 'kantsu', open: true };
  } else {
    // 暗杠 / 明杠：手牌取 3 张，新增一个杠子
    let removed = 0;
    hand = hand.filter((t) => {
      if (removed < 3 && tileIndex(t) === idx) {
        removed++;
        return false;
      }
      return true;
    });
    if (removed !== 3) throw new Error('杠需要手牌中有 3 张相同的牌');
    const mentsu: Mentsu = {
      kind: 'kantsu',
      start: idx,
      open: kind !== 'ankan',
      names: [],
    };
    melds.push(mentsu);
  }

  // 包牌：明杠（minkan）凑齐大三元/大四喜 → 责任者 = 被鸣牌者
  const paoClaim =
    kind === 'minkan' && state.lastDiscard
      ? detectPao(seat, state.lastDiscard.bySeat, melds)
      : null;

  // ⚠️ 四杠散了：杠子累计 ≥4 且非同一家 → 立即途中流局（在岭上摸牌之前成立）
  {
    const players = state.players.map((p) => (p.seat === seat ? { ...p, melds } : p));
    if (settleDrawModule.checkFourKans({ ...state, players })) {
      return abortiveDraw({ ...state, players }, 'four_kans');
    }
  }

  // 岭上摸牌
  const dead = [...state.wall.dead];
  const drawn = dead.shift();
  if (!drawn) throw new Error('岭上牌已用尽');
  hand.push(drawn);
  hand.sort(compareTile);

  // 翻开新宝牌指示牌
  const newDora = state.wall.doraRevealed + 1;

  const players = state.players.map((p) =>
    p.seat === seat ? { ...p, hand, melds } : p,
  );

  return {
    ...state,
    phase: 'discard',
    players,
    pao: paoClaim ? [...(state.pao ?? []), paoClaim] : state.pao,
    wall: {
      ...state.wall,
      dead,
      doraRevealed: Math.min(newDora, 5),
    },
  };
}

/**
 * 跳过（无人鸣牌/荣和）→ 进入下一家摸牌
 *
 * ⚠️ 语义约定：**调用方负责设置 activeSeat**（因为"下一家"的上下文由调用方决定）。
 *    本函数只做"摸牌"这一步。
 *
 * 早期版本在这里又调了一次 nextSeat()，而 runner 的 claim 阶段已经把
 * activeSeat 设为下家了 → 座位被推进两次，导致两家永远不摸牌
 * （实测：座位 1/3 河为 0，牌数分布异常）。
 */
function doPass(state: GameState): GameState {
  return apply({ ...state, lastDiscard: null }, { type: 'draw' });
}

// ════════════════════════════════════════════════════════════════
// 工具
// ════════════════════════════════════════════════════════════════

/** 手牌排序（按 34 索引） */
export function compareTile(a: Tile, b: Tile): number {
  return tileIndex(a) - tileIndex(b) || a.id - b.id;
}

/** 拿到状态摘要（调试用） */
export function describeState(state: GameState): string {
  const p = state.players[state.activeSeat];
  return `[${state.phase}] turn=${state.turn} active=${state.activeSeat} hand=${p.hand.length} live=${state.wall.live.length}`;
}

/** 从手牌中移除指定的牌（按 id） */
export function removeTile(hand: readonly Tile[], tile: Tile): Tile[] {
  const idx = hand.findIndex((t) => t.id === tile.id);
  if (idx < 0) throw new Error('手牌中没有这张牌');
  return [...hand.slice(0, idx), ...hand.slice(idx + 1)];
}

/** 统计手牌中某种牌的数量 */
export function countInHand(hand: readonly Tile[], index: number): number {
  return hand.filter((t) => tileIndex(t) === index).length;
}

/** 剩余可摸的牌数 */
export function tilesRemaining(state: GameState): number {
  return state.wall.live.length;
}

/** 可选：暴露常量给调用方 */
export const CONSTANTS = {
  COPIES_PER_TILE,
  INITIAL_HAND_SIZE,
  DEAD_WALL_SIZE,
  RIICHI_STICK_VALUE,
  STARTING_SCORE,
} as const;


// ════════════════════════════════════════════════════════════════
// 和牌结算（供 doTsumo / doRon 调用）
// ════════════════════════════════════════════════════════════════

/**
 * 计算和牌的点数变化并应用到状态
 *
 * ⚠️ 这里做完整的「役种 → 符数 → 点数 → 点数移动」，
 *    包括本场 bonus 和供托归和牌者。
 */
function settleWinInState(
  state: GameState,
  winner: Seat,
  method: 'tsumo' | 'ron',
  loser: Seat | null,
  winTile: Tile,
): { state: GameState; changes: number[] } {
  const changes = [0, 0, 0, 0];

  try {
    // 动态导入避免模块循环依赖
    const detail = detailModule;
    const d = detail.computeWinDetail(state, winner, method, loser, winTile);
    if (d) {
      for (let s = 0; s < 4; s++) changes[s] = d.scoreChanges[s] ?? 0;
    } else {
      // 无法计算（无役等）→ 用保底值
      const base = method === 'ron' ? 1000 : 500;
      if (method === 'ron' && loser !== null) {
        changes[winner] += base;
        changes[loser] -= base;
      }
    }
  } catch {
    // 兜底：至少保证点数守恒（供托在下方统一处理）
    const base = 1000;
    if (method === 'ron' && loser !== null) {
      changes[winner] += base;
      changes[loser] -= base;
    } else {
      for (let s = 0; s < 4; s++) {
        if (s === winner) continue;
        changes[s] -= 400;
        changes[winner] += 400;
      }
    }
  }

  // ⚠️ 兜底：确保有非零变化（否则 UI 显示异常）
  if (changes.every((c) => c === 0)) {
    const base = method === 'ron' ? 1000 : 300;
    if (method === 'ron' && loser !== null) {
      changes[winner] += base;
      changes[loser] -= base;
    }
  }

  // ⚠️⚠️ 供托（立直棒）**必须在所有分支后统一加给和牌者**
  //    立直时已从分数扣走 1000 并记入 riichiSticks，
  //    和牌时必须归还，否则点数不守恒（踩过：总和 99000 ≠ 100000）。
  const sticks = state.round.riichiSticks;
  if (sticks > 0) {
    changes[winner] += sticks * 1000;
    // 供托池已清空（下一局归零）
  }

  return { state: applyChangesToState(state, changes), changes };
}

/** 把 changes 应用到分数 */
function applyChangesToState(state: GameState, changes: readonly number[]): GameState {
  return {
    ...state,
    players: state.players.map((p) => ({
      ...p,
      score: p.score + (changes[p.seat] ?? 0),
    })),
  };

}
